/**
 * t187u: offline clock in / out and breaks (docs/offline.md §3.4). Pure rules shared by the API (which decides) and the
 * clients (which show the same thing in advance): the corrected time of a punch made with no signal, the checks that
 * decide whether it is auto-accepted or flagged for a manager, the optimistic clock state while punches wait to be
 * sent, and what a refusal means to the person (with the fix).
 */
import { evaluateGeofence, formatDistance, type Geofence, type GeoReading, type GeoStatus } from './clock';
import type { CurrentTime, TimeEntry } from './types';

/** Decision 1 (accepted by Daniel): auto-accept within 12 h, clock skew under 5 min, location inside the fence. */
export const OFFLINE_CLOCK_RULES = { maxDelayMs: 12 * 3600_000, maxSkewMs: 5 * 60_000, futureToleranceMs: 60_000 } as const;
/** Clock ops stay on the device for 30 days (other ops 7). */
export const CLOCK_OP_TTL_MS = 30 * 24 * 3600_000;

export type ClockOpKind = 'clock.in' | 'clock.out' | 'break.start' | 'break.end';
export const CLOCK_OP_KINDS: readonly ClockOpKind[] = ['clock.in', 'clock.out', 'break.start', 'break.end'];
export const CLOCK_OP_LABEL: Record<ClockOpKind, string> = { 'clock.in': 'Clock in', 'clock.out': 'Clock out', 'break.start': 'Break', 'break.end': 'End break' };
/** The clock card / top bar action for each op kind. */
export const CLOCK_ACTION_OP = { in: 'clock.in', out: 'clock.out', break: 'break.start', resume: 'break.end' } as const satisfies Record<string, ClockOpKind>;

export type ClockReviewReason = 'late_sync' | 'clock_skew' | 'outside_geofence' | 'no_location' | 'out_of_order';

export interface CorrectedPunch {
  /** Epoch ms the punch is recorded at. */
  at: number;
  /** server receive time − device_time_sent (the phone clock's error plus transit); null when the send time is missing. */
  skewMs: number | null;
  /** How long after the tap it arrived. */
  delayMs: number;
  /** The corrected time is in the future (the phone's clock was changed between tap and send): refused. */
  future: boolean;
}

/** corrected = device_time + (server receive time − device_time_sent): cancels any constant error in the phone's clock. */
export function correctedPunchTime(deviceTime: string, deviceTimeSent: string | null | undefined, serverNow: number, rules = OFFLINE_CLOCK_RULES): CorrectedPunch {
  const tap = Date.parse(deviceTime);
  const sent = deviceTimeSent ? Date.parse(deviceTimeSent) : NaN;
  const skewMs = Number.isFinite(sent) ? serverNow - sent : null;
  const at = tap + (skewMs ?? 0);
  return { at, skewMs, delayMs: serverNow - at, future: at > serverNow + rules.futureToleranceMs };
}

/** Why an offline punch needs a manager. Empty = auto-accepted, exactly like an online punch. */
export function punchReviewReasons(p: { delayMs: number; skewMs: number | null; geo?: GeoStatus | null; outOfOrder?: boolean }, rules = OFFLINE_CLOCK_RULES): ClockReviewReason[] {
  const out: ClockReviewReason[] = [];
  if (p.delayMs > rules.maxDelayMs) out.push('late_sync');
  if (p.skewMs == null || Math.abs(p.skewMs) > rules.maxSkewMs) out.push('clock_skew');
  if (p.geo === 'outside') out.push('outside_geofence');
  if (p.geo === 'unknown') out.push('no_location');
  if (p.outOfOrder) out.push('out_of_order');
  return out;
}

export interface OfflinePunchCheck extends CorrectedPunch {
  geo: GeoStatus;
  distance_m: number | null;
  reasons: ClockReviewReason[];
}
/**
 * The whole check for one offline punch. Offline punches are never refused for the geofence (refusing after the fact
 * loses the record): outside or unknown is flagged instead. Only clock-in is checked against the fence.
 */
export function checkOfflinePunch(i: {
  deviceTime: string; deviceTimeSent?: string | null; serverNow: number; kind: ClockOpKind;
  fence?: Geofence | null; reading?: GeoReading | null; previousPunchAt?: number | null;
}, rules = OFFLINE_CLOCK_RULES): OfflinePunchCheck {
  const c = correctedPunchTime(i.deviceTime, i.deviceTimeSent, i.serverNow, rules);
  const g = i.kind === 'clock.in' ? evaluateGeofence(i.fence ?? null, i.reading, 'out') : { geo: 'not_checked' as GeoStatus, distance_m: null };
  const outOfOrder = i.previousPunchAt != null && c.at < i.previousPunchAt;
  return { ...c, geo: g.geo, distance_m: g.distance_m, reasons: punchReviewReasons({ delayMs: c.delayMs, skewMs: c.skewMs, geo: g.geo, outOfOrder }, rules) };
}

/** Short chips for a manager: "Offline · 14 h late", "Clock skew 9 min", "Outside fence 320 m". */
export function reviewChips(e: Pick<TimeEntry, 'review_reasons' | 'sync_delay_s' | 'clock_skew_s' | 'clock_in_distance_m'>): string[] {
  return (e.review_reasons ?? []).map(r => {
    switch (r) {
      case 'late_sync': return `Offline · ${Math.max(12, Math.round((e.sync_delay_s ?? 0) / 3600))} h late`;
      case 'clock_skew': return e.clock_skew_s == null ? 'Phone clock not checked' : `Clock skew ${Math.max(5, Math.round(Math.abs(e.clock_skew_s) / 60))} min`;
      case 'outside_geofence': return e.clock_in_distance_m != null ? `Outside fence ${formatDistance(e.clock_in_distance_m)}` : 'Outside fence';
      case 'no_location': return 'No location';
      case 'out_of_order': return 'Out of order';
      case 'conflicts_with_edit': return 'Conflicts with an edit';
      default: return r;
    }
  });
}
/** Flagged entries can't be bulk-approved (or exported) until a manager approves them one by one. */
export const needsReview = (e: Pick<TimeEntry, 'review_reasons' | 'status'>) => Boolean(e.review_reasons?.length) && e.status !== 'approved' && e.status !== 'rejected';

// ------------------------------------------------------------------------------------------------ client side
/** What the clock overlay needs from a queued op. */
export interface PendingClockOp {
  id: string;
  kind: ClockOpKind;
  /** Device time of the tap (ISO). */
  deviceTime: string;
  state: 'queued' | 'sending' | 'failed' | 'conflict';
  shiftId?: string | null;
  locationId?: string | null;
}
export type ClockOpState = 'out' | 'in' | 'break';
const stateOf = (e: TimeEntry | null): ClockOpState => (!e ? 'out' : e.breaks.some(b => !b.ended_at) ? 'break' : 'in');
/** Which ops make sense from a state (duplicates are refused before they're queued). */
export function clockOpAllowed(state: ClockOpState, kind: ClockOpKind): boolean {
  if (kind === 'clock.in') return state === 'out';
  if (kind === 'clock.out') return state !== 'out';
  if (kind === 'break.start') return state === 'in';
  return state === 'break';
}

/**
 * The clock as it will be once the waiting punches are sent: the server's state with the queued / sending ops applied
 * in order. Failed ops are left out, so a refused punch rolls the local state back by itself; an op that makes no
 * sense at that point (e.g. a clock-out after a refused clock-in) is skipped too.
 */
export function applyPendingClock<T extends Pick<CurrentTime, 'entry'>>(cur: T, ops: readonly PendingClockOp[]): T & { pendingOps: number } {
  let entry = cur.entry ? { ...cur.entry, breaks: [...cur.entry.breaks] } : null;
  let applied = 0;
  for (const op of [...ops].sort((a, b) => a.deviceTime.localeCompare(b.deviceTime))) {
    if (op.state === 'failed' || op.state === 'conflict') continue;
    if (!clockOpAllowed(stateOf(entry), op.kind)) continue;
    applied += 1;
    if (op.kind === 'clock.in') {
      entry = {
        id: `pending:${op.id}`, user_id: '', shift_id: op.shiftId ?? null, location_id: op.locationId ?? '', position_id: null, clock_in_at: op.deviceTime, clock_out_at: null,
        status: 'open', source: 'mobile', notes: null, manager_note: null, approved_by: null, approved_at: null, version: 0, breaks: [], clock_in_offline: true,
      };
    } else if (op.kind === 'clock.out') entry = null;
    else if (op.kind === 'break.start') entry = { ...entry!, breaks: [...entry!.breaks, { id: `pending:${op.id}`, started_at: op.deviceTime, ended_at: null, paid: false }] };
    else entry = { ...entry!, breaks: entry!.breaks.map(b => (b.ended_at ? b : { ...b, ended_at: op.deviceTime })) };
  }
  return { ...cur, entry, pendingOps: applied };
}

/** A refused punch, in words, with what to do about it. */
export interface ClockOpProblem { message: string; fix: string }
export function clockOpProblem(kind: ClockOpKind, status: number | null, body: unknown): ClockOpProblem {
  const b = (body ?? {}) as { code?: string; message?: string | string[] };
  const raw = Array.isArray(b.message) ? b.message.join('; ') : b.message ?? '';
  const code = b.code ?? '';
  if (code === 'already_clocked_in') return { message: 'You were already clocked in', fix: 'Discard this one; your earlier clock-in stands.' };
  if (code === 'not_clocked_in' || /not clocked in/i.test(raw)) return { message: 'You weren\'t clocked in when this arrived', fix: 'Discard it and ask your manager to correct your timesheet.' };
  if (code === 'clock_future') return { message: 'This phone\'s clock was changed', fix: 'Set the date and time to automatic, then Retry.' };
  if (code === 'location_required' || code === 'location_unavailable') return { message: raw || 'Choose the building', fix: 'Discard it and clock in again to pick the building.' };
  if (/published shifts|no shift/i.test(raw)) return { message: 'That shift isn\'t yours any more', fix: 'Discard it and clock in again without the shift.' };
  if (/before your shift starts/i.test(raw)) return { message: raw, fix: 'Discard it and clock in nearer the start.' };
  if (status === 403) return { message: raw || 'Not allowed', fix: 'Discard it and ask your manager.' };
  return { message: raw || `${CLOCK_OP_LABEL[kind]} wasn't accepted`, fix: 'Retry, or Discard it.' };
}
